import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  Folder, 
  FolderOpen, 
  FileText, 
  Plane, 
  Upload, 
  Search, 
  ChevronRight, 
  MoreHorizontal,
  Eye,
  Download,
  Trash2,
  Calendar,
  User,
  MapPin,
  RefreshCw,
  Plus,
  File,
  Building2,
  Shield
} from "lucide-react";
import { format } from "date-fns";
import { logActivity } from "@/lib/activityLogger";

interface InquiryWithFiles {
  id: string;
  name: string;
  email: string;
  occasion: string | null;
  event_date: string;
  venue_address: string;
  status: string;
  created_at: string;
  files: FileInfo[];
}

interface FileInfo {
  id: string;
  name: string;
  storage_path: string;
  type: 'contract' | 'itinerary' | 'attachment' | 'admin';
  status: string;
  created_at: string;
  size?: number;
}

interface AdminFile {
  id: string;
  name: string;
  storage_path: string;
  created_at: string;
  size?: number;
}

export function FilesManager() {
  const { toast } = useToast();
  const [inquiries, setInquiries] = useState<InquiryWithFiles[]>([]);
  const [adminFiles, setAdminFiles] = useState<AdminFile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [adminFolderExpanded, setAdminFolderExpanded] = useState(false);
  const [previewFile, setPreviewFile] = useState<{ html: string; name: string } | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ file: FileInfo; inquiryId: string } | null>(null);
  const [deleteAdminConfirm, setDeleteAdminConfirm] = useState<AdminFile | null>(null);
  const [uploadingTo, setUploadingTo] = useState<string | null>(null);
  const [uploadingAdmin, setUploadingAdmin] = useState(false);
  const adminFileInputRef = React.useRef<HTMLInputElement>(null);

  const fetchAdminFiles = useCallback(async () => {
    try {
      const { data, error } = await supabase.storage
        .from("contracts")
        .list("admin", { sortBy: { column: "created_at", order: "desc" } });

      if (error) {
        // If folder doesn't exist yet, that's okay
        if (!error.message.includes("not found")) {
          throw error;
        }
        setAdminFiles([]);
        return;
      }

      const files: AdminFile[] = (data || [])
        .filter((f) => f.name !== ".emptyFolderPlaceholder")
        .map((f) => ({
          id: f.id || f.name,
          name: f.name,
          storage_path: `admin/${f.name}`,
          created_at: f.created_at || new Date().toISOString(),
          size: f.metadata?.size,
        }));

      setAdminFiles(files);
    } catch (error: any) {
      console.error("Failed to load admin files:", error);
    }
  }, []);

  const fetchInquiriesWithFiles = useCallback(async () => {
    setIsLoading(true);
    try {
      // Fetch all inquiries and admin files in parallel
      const [inquiriesRes, contractsRes] = await Promise.all([
        supabase
          .from("contact_submissions")
          .select("id, name, email, occasion, event_date, venue_address, status, created_at")
          .order("created_at", { ascending: false }),
        supabase
          .from("inquiry_contracts")
          .select("id, inquiry_id, contract_number, storage_path, status, created_at, event_details"),
      ]);

      if (inquiriesRes.error) throw inquiriesRes.error;
      if (contractsRes.error) throw contractsRes.error;

      // Map files to inquiries
      const inquiriesWithFiles: InquiryWithFiles[] = (inquiriesRes.data || []).map((inquiry) => {
        const files: FileInfo[] = (contractsRes.data || [])
          .filter((c) => c.inquiry_id === inquiry.id && c.storage_path)
          .map((c) => {
            const eventDetails = c.event_details as Record<string, unknown> | null;
            const isItinerary = c.contract_number?.startsWith('ITIN-') || 
              (eventDetails && eventDetails.type === 'travel_itinerary');
            return {
              id: c.id,
              name: c.contract_number,
              storage_path: c.storage_path!,
              type: isItinerary ? 'itinerary' as const : 'contract' as const,
              status: c.status,
              created_at: c.created_at,
            };
          });
        return {
          ...inquiry,
          files,
        };
      });

      setInquiries(inquiriesWithFiles);
      await fetchAdminFiles();
    } catch (error: any) {
      toast({ 
        title: "Failed to load files", 
        description: error.message, 
        variant: "destructive" 
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast, fetchAdminFiles]);

  useEffect(() => {
    fetchInquiriesWithFiles();
  }, [fetchInquiriesWithFiles]);

  const toggleFolder = (inquiryId: string) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(inquiryId)) {
        next.delete(inquiryId);
      } else {
        next.add(inquiryId);
      }
      return next;
    });
  };

  const filteredInquiries = inquiries.filter((inquiry) => {
    const query = searchQuery.toLowerCase();
    return (
      inquiry.name.toLowerCase().includes(query) ||
      inquiry.email.toLowerCase().includes(query) ||
      inquiry.occasion?.toLowerCase().includes(query) ||
      inquiry.venue_address.toLowerCase().includes(query) ||
      inquiry.files.some((f) => f.name.toLowerCase().includes(query))
    );
  });

  const previewFileHandler = async (file: FileInfo) => {
    try {
      const { data, error } = await supabase.storage
        .from("contracts")
        .download(file.storage_path);
      
      if (error) throw error;
      
      const htmlContent = await data.text();
      setPreviewFile({ html: htmlContent, name: file.name });
    } catch (error: any) {
      toast({ 
        title: "Preview failed", 
        description: error.message, 
        variant: "destructive" 
      });
    }
  };

  const downloadFile = async (file: FileInfo) => {
    try {
      const { data, error } = await supabase.storage
        .from("contracts")
        .download(file.storage_path);
      
      if (error) throw error;

      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${file.name}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error: any) {
      toast({ 
        title: "Download failed", 
        description: error.message, 
        variant: "destructive" 
      });
    }
  };

  const handleDeleteFile = async () => {
    if (!deleteConfirm) return;

    const { file, inquiryId } = deleteConfirm;
    
    try {
      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from("contracts")
        .remove([file.storage_path]);

      if (storageError) throw storageError;

      // Delete from database
      const { error: dbError } = await supabase
        .from("inquiry_contracts")
        .delete()
        .eq("id", file.id);

      if (dbError) throw dbError;

      // Log activity
      logActivity({
        inquiryId,
        action: 'document_deleted',
        description: `Deleted ${file.type} ${file.name} from Files Manager`,
        metadata: { fileName: file.name, fileType: file.type }
      });

      toast({ title: "File deleted successfully" });
      fetchInquiriesWithFiles();
    } catch (error: any) {
      toast({ 
        title: "Delete failed", 
        description: error.message, 
        variant: "destructive" 
      });
    } finally {
      setDeleteConfirm(null);
    }
  };

  const handleFileUpload = async (inquiryId: string, files: FileList | null) => {
    if (!files || files.length === 0) return;

    setUploadingTo(inquiryId);

    try {
      for (const file of Array.from(files)) {
        const timestamp = Date.now();
        const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
        const storagePath = `${inquiryId}/uploads/${timestamp}-${safeName}`;

        // Upload to storage
        const { error: uploadError } = await supabase.storage
          .from("contracts")
          .upload(storagePath, file);

        if (uploadError) throw uploadError;

        // Create database record
        const { error: dbError } = await supabase
          .from("inquiry_contracts")
          .insert({
            inquiry_id: inquiryId,
            contract_number: `FILE-${timestamp.toString().slice(-6)}`,
            client_name: "Uploaded File",
            client_email: "upload@system",
            event_details: { type: 'uploaded_file', original_name: file.name },
            packages: [],
            total_amount: 0,
            storage_path: storagePath,
            status: 'draft'
          });

        if (dbError) throw dbError;

        // Log activity
        logActivity({
          inquiryId,
          action: 'document_created',
          description: `Uploaded file: ${file.name}`,
          metadata: { fileName: file.name, fileSize: file.size }
        });
      }

      toast({ title: "File(s) uploaded successfully" });
      fetchInquiriesWithFiles();
    } catch (error: any) {
      toast({ 
        title: "Upload failed", 
        description: error.message, 
        variant: "destructive" 
      });
    } finally {
      setUploadingTo(null);
    }
  };

  const handleAdminFileUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    setUploadingAdmin(true);

    try {
      for (const file of Array.from(files)) {
        const timestamp = Date.now();
        const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
        const storagePath = `admin/${timestamp}-${safeName}`;

        // Upload to storage
        const { error: uploadError } = await supabase.storage
          .from("contracts")
          .upload(storagePath, file);

        if (uploadError) throw uploadError;
      }

      toast({ title: "Admin file(s) uploaded successfully" });
      fetchAdminFiles();
    } catch (error: any) {
      toast({ 
        title: "Upload failed", 
        description: error.message, 
        variant: "destructive" 
      });
    } finally {
      setUploadingAdmin(false);
    }
  };

  const handleDeleteAdminFile = async () => {
    if (!deleteAdminConfirm) return;

    try {
      const { error } = await supabase.storage
        .from("contracts")
        .remove([deleteAdminConfirm.storage_path]);

      if (error) throw error;

      toast({ title: "Admin file deleted successfully" });
      fetchAdminFiles();
    } catch (error: any) {
      toast({ 
        title: "Delete failed", 
        description: error.message, 
        variant: "destructive" 
      });
    } finally {
      setDeleteAdminConfirm(null);
    }
  };

  const downloadAdminFile = async (file: AdminFile) => {
    try {
      const { data, error } = await supabase.storage
        .from("contracts")
        .download(file.storage_path);
      
      if (error) throw error;

      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      // Extract original filename from storage path
      const dashIndex = file.name.indexOf('-');
      const originalName = dashIndex > 0 ? file.name.substring(dashIndex + 1) : file.name;
      a.download = originalName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error: any) {
      toast({ 
        title: "Download failed", 
        description: error.message, 
        variant: "destructive" 
      });
    }
  };

  const getFileIcon = (type: string) => {
    switch (type) {
      case 'itinerary':
        return <Plane className="h-4 w-4 text-primary" />;
      case 'contract':
        return <FileText className="h-4 w-4 text-amber-500" />;
      case 'admin':
        return <Building2 className="h-4 w-4 text-purple-500" />;
      default:
        return <File className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      signed: "bg-green-600 text-white",
      sent: "bg-blue-500/20 text-blue-700 border-blue-500/30",
      draft: "",
    };
    return (
      <Badge 
        variant={status === 'draft' ? 'outline' : 'default'} 
        className={`text-[10px] ${styles[status] || ''}`}
      >
        {status}
      </Badge>
    );
  };

  const getAdminFileName = (name: string) => {
    const dashIndex = name.indexOf('-');
    return dashIndex > 0 ? name.substring(dashIndex + 1) : name;
  };

  const totalFiles = inquiries.reduce((sum, inq) => sum + inq.files.length, 0);
  const inquiriesWithFilesCount = inquiries.filter((inq) => inq.files.length > 0).length;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Folder className="h-5 w-5" />
              Files Manager
            </CardTitle>
            <CardDescription>
              System-wide file storage organized by inquiry ({totalFiles} files across {inquiriesWithFilesCount} inquiries)
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={fetchInquiriesWithFiles} disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, email, occasion, venue, or file name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Admin Folder Section */}
        <div className="mb-4">
          <Collapsible open={adminFolderExpanded} onOpenChange={setAdminFolderExpanded}>
            <div className="rounded-lg border-2 border-dashed border-primary/30 bg-primary/5 hover:bg-primary/10 transition-colors">
              <CollapsibleTrigger asChild>
                <div className="flex items-center gap-3 p-3 cursor-pointer w-full">
                  <div className="flex items-center gap-2">
                    <ChevronRight 
                      className={`h-4 w-4 text-primary transition-transform ${
                        adminFolderExpanded ? 'rotate-90' : ''
                      }`} 
                    />
                    {adminFolderExpanded ? (
                      <FolderOpen className="h-5 w-5 text-primary" />
                    ) : (
                      <Shield className="h-5 w-5 text-primary" />
                    )}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-sm text-primary">
                        Admin Files
                      </span>
                      <Badge variant="outline" className="text-[10px] border-primary/50">
                        {adminFiles.length} {adminFiles.length === 1 ? 'file' : 'files'}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Global files accessible in all email composers
                    </p>
                  </div>
                </div>
              </CollapsibleTrigger>

              <CollapsibleContent>
                <div className="border-t border-primary/20 bg-background/50 p-3 space-y-2">
                  {/* Admin files list */}
                  {adminFiles.length > 0 ? (
                    <div className="space-y-1">
                      {adminFiles.map((file) => (
                        <div 
                          key={file.id}
                          className="flex items-center justify-between p-2 rounded bg-background border hover:border-primary/30 transition-colors"
                        >
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-primary" />
                            <span className="text-sm font-medium">{getAdminFileName(file.name)}</span>
                            <span className="text-xs text-muted-foreground">
                              {format(new Date(file.created_at), "MMM d, h:mm a")}
                            </span>
                          </div>

                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => downloadAdminFile(file)}>
                                <Download className="mr-2 h-4 w-4" /> Download
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                className="text-destructive focus:text-destructive"
                                onClick={() => setDeleteAdminConfirm(file)}
                              >
                                <Trash2 className="mr-2 h-4 w-4" /> Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground text-center py-4">
                      No admin files yet
                    </p>
                  )}

                  {/* Upload button */}
                  <div className="pt-2 border-t">
                    <input
                      ref={adminFileInputRef}
                      type="file"
                      multiple
                      className="hidden"
                      onChange={(e) => handleAdminFileUpload(e.target.files)}
                    />
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full"
                      onClick={() => adminFileInputRef.current?.click()}
                      disabled={uploadingAdmin}
                    >
                      {uploadingAdmin ? (
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Plus className="h-4 w-4 mr-2" />
                      )}
                      Upload Admin File
                    </Button>
                  </div>
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>
        </div>

        {/* Folder List */}
        <ScrollArea className="h-[500px]">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : filteredInquiries.length === 0 && !searchQuery ? (
            <div className="text-center py-12 text-muted-foreground">
              No inquiries yet
            </div>
          ) : filteredInquiries.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              No matching inquiries found
            </div>
          ) : (
            <div className="space-y-1">
              {filteredInquiries.map((inquiry) => {
                const isExpanded = expandedFolders.has(inquiry.id);
                const hasFiles = inquiry.files.length > 0;

                return (
                  <Collapsible
                    key={inquiry.id}
                    open={isExpanded}
                    onOpenChange={() => toggleFolder(inquiry.id)}
                  >
                    <div className="rounded-lg border bg-card hover:bg-muted/30 transition-colors">
                      <CollapsibleTrigger asChild>
                        <div className="flex items-center gap-3 p-3 cursor-pointer w-full">
                          <div className="flex items-center gap-2">
                            <ChevronRight 
                              className={`h-4 w-4 text-muted-foreground transition-transform ${
                                isExpanded ? 'rotate-90' : ''
                              }`} 
                            />
                            {isExpanded ? (
                              <FolderOpen className="h-5 w-5 text-primary" />
                            ) : (
                              <Folder className={`h-5 w-5 ${hasFiles ? 'text-amber-500' : 'text-muted-foreground'}`} />
                            )}
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm truncate">
                                {inquiry.name}
                              </span>
                              <Badge variant="outline" className="text-[10px]">
                                {inquiry.files.length} {inquiry.files.length === 1 ? 'file' : 'files'}
                              </Badge>
                              <Badge 
                                variant="secondary" 
                                className={`text-[10px] ${
                                  inquiry.status === 'confirmed' ? 'bg-green-500/20 text-green-700' :
                                  inquiry.status === 'pending' ? 'bg-yellow-500/20 text-yellow-700' :
                                  ''
                                }`}
                              >
                                {inquiry.status}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                              <span className="flex items-center gap-1">
                                <User className="h-3 w-3" />
                                {inquiry.email}
                              </span>
                              <span className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                {format(new Date(inquiry.event_date), "MMM d, yyyy")}
                              </span>
                              {inquiry.occasion && (
                                <span className="truncate max-w-[150px]">
                                  {inquiry.occasion}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </CollapsibleTrigger>

                      <CollapsibleContent>
                        <div className="border-t bg-muted/20 p-3 space-y-2">
                          {/* Venue info */}
                          <div className="flex items-center gap-1 text-xs text-muted-foreground mb-3">
                            <MapPin className="h-3 w-3" />
                            {inquiry.venue_address}
                          </div>

                          {/* Files list */}
                          {inquiry.files.length > 0 ? (
                            <div className="space-y-1">
                              {inquiry.files.map((file) => (
                                <div 
                                  key={file.id}
                                  className="flex items-center justify-between p-2 rounded bg-background border hover:border-primary/30 transition-colors"
                                >
                                  <div className="flex items-center gap-2">
                                    {getFileIcon(file.type)}
                                    <span className="text-sm font-medium">{file.name}</span>
                                    {getStatusBadge(file.status)}
                                    <span className="text-xs text-muted-foreground">
                                      {format(new Date(file.created_at), "MMM d, h:mm a")}
                                    </span>
                                  </div>

                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                                        <MoreHorizontal className="h-4 w-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      <DropdownMenuItem onClick={() => previewFileHandler(file)}>
                                        <Eye className="mr-2 h-4 w-4" /> Preview
                                      </DropdownMenuItem>
                                      <DropdownMenuItem onClick={() => downloadFile(file)}>
                                        <Download className="mr-2 h-4 w-4" /> Download
                                      </DropdownMenuItem>
                                      <DropdownMenuItem 
                                        onClick={() => setDeleteConfirm({ file, inquiryId: inquiry.id })}
                                        className="text-destructive focus:text-destructive"
                                      >
                                        <Trash2 className="mr-2 h-4 w-4" /> Delete
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-muted-foreground text-center py-2">
                              No files in this folder
                            </p>
                          )}

                          {/* Upload button */}
                          <div className="pt-2">
                            <label className="cursor-pointer">
                              <input
                                type="file"
                                multiple
                                className="hidden"
                                onChange={(e) => handleFileUpload(inquiry.id, e.target.files)}
                                disabled={uploadingTo === inquiry.id}
                              />
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="w-full"
                                disabled={uploadingTo === inquiry.id}
                                asChild
                              >
                                <span>
                                  {uploadingTo === inquiry.id ? (
                                    <>
                                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                                      Uploading...
                                    </>
                                  ) : (
                                    <>
                                      <Plus className="mr-2 h-4 w-4" />
                                      Add Files
                                    </>
                                  )}
                                </span>
                              </Button>
                            </label>
                          </div>
                        </div>
                      </CollapsibleContent>
                    </div>
                  </Collapsible>
                );
              })}
            </div>
          )}
        </ScrollArea>

        {/* Preview Dialog */}
        <Dialog open={!!previewFile} onOpenChange={() => setPreviewFile(null)}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
            <DialogHeader>
              <DialogTitle>{previewFile?.name}</DialogTitle>
              <DialogDescription>Document Preview</DialogDescription>
            </DialogHeader>
            <ScrollArea className="h-[70vh]">
              {previewFile && (
                <iframe
                  srcDoc={previewFile.html}
                  className="w-full h-full min-h-[60vh] border rounded"
                  title="Document Preview"
                />
              )}
            </ScrollArea>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete File</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete "{deleteConfirm?.file.name}"? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteFile} className="bg-destructive text-destructive-foreground">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Admin Delete Confirmation */}
        <AlertDialog open={!!deleteAdminConfirm} onOpenChange={() => setDeleteAdminConfirm(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Admin File</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete "{deleteAdminConfirm && getAdminFileName(deleteAdminConfirm.name)}"? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteAdminFile} className="bg-destructive text-destructive-foreground">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}
